import * as MoneyAccountAction from './money-accounts.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {MoneyAccount, MoneyAccountDocumentType} from '@accounting/_common/models/money-account.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const moneyAccountsFeatureKey = 'moneyAccounts';

export function sortByDate(a: MoneyAccount, b: MoneyAccount) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'name', 'ASC');
}

export const adapter: EntityAdapter<MoneyAccount> = createEntityAdapter<MoneyAccount>({
    sortComparer: sortByDate
});

export const typedListInitialState: EntityStateModel<MoneyAccount, any> = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export interface MoneyAccountsState {
    BANK: EntityStateModel<MoneyAccount, any>,
    CASH: EntityStateModel<MoneyAccount, any>,
    ALL: EntityStateModel<MoneyAccount, any>
}

export const initialState: MoneyAccountsState = {
    BANK: {...typedListInitialState},
    CASH: {...typedListInitialState},
    ALL: {...typedListInitialState}
};


export const moneyAccountsReducer = createReducer(
    initialState,

    on(MoneyAccountAction.LoadList, (state, action) => {
        return ({
            ...state,
            [action.payload.moneyAccountDocumentType]: {
                ...state[action.payload.moneyAccountDocumentType],
                listLoading: StoredComponentHelperService.updateListLoading(action.payload, state[action.payload.moneyAccountDocumentType].ids)
            }
        });
    }),

    on(MoneyAccountAction.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.moneyAccountDocumentType]: adapter.upsertMany(action.payload.items, {
                ...state[action.payload.moneyAccountDocumentType],
                pagination: action.payload.pagination,
                listLoading: undefined
            }),
        });
    }),


    on(MoneyAccountAction.LoadListFailure, (state, action) => ({
        ...state,
        [action.payload.moneyAccountDocumentType]: {
            ...state[action.payload.moneyAccountDocumentType],
            listLoading: undefined
        }
    })),


    on(MoneyAccountAction.SetPagination, (state, action) => ({
        ...state,
        [action.payload.moneyAccountDocumentType]: {
            ...state[action.payload.moneyAccountDocumentType],
            pagination: {
                ...state[action.payload.moneyAccountDocumentType].pagination,
                number: action.payload.next ? StoredComponentHelperService.setNextPage(state[action.payload.moneyAccountDocumentType].pagination, state[action.payload.moneyAccountDocumentType].ids?.length) : 0
            }
        }
    })),

    on(MoneyAccountAction.SetLoading, (state, action) => ({
        ...state,
        [action.payload.moneyAccountDocumentType]: {
            ...state[action.payload.moneyAccountDocumentType],
            loading: action.payload.loading
        }
    })),

    on(MoneyAccountAction.SetItemsLoading, (state, action) => ({
        ...state,
        [action.payload.moneyAccountDocumentType]: {
            ...state[action.payload.moneyAccountDocumentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.moneyAccountDocumentType].itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    on(MoneyAccountAction.UpdateItemSuccess, (state, action) => {
        return {
            ...state,
            [action.payload.moneyAccountDocumentType]: adapter.setOne(action.payload.item, {
                ...state[action.payload.moneyAccountDocumentType],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.moneyAccountDocumentType].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state[action.payload.moneyAccountDocumentType].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.moneyAccountDocumentType].pagination, state[action.payload.moneyAccountDocumentType]?.ids)
                }
            })
        };
    }),

    on(MoneyAccountAction.DeleteItem, (state, action) => ({
        ...state,
        [action.payload.moneyAccountDocumentType]: {
            ...state[action.payload.moneyAccountDocumentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.moneyAccountDocumentType].itemsLoading, action.payload.item.id!, true)
        }
    })),

    on(MoneyAccountAction.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.moneyAccountDocumentType]: adapter.removeOne(action.payload.item.id!, {
                ...state[action.payload.moneyAccountDocumentType],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.moneyAccountDocumentType].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state[action.payload.moneyAccountDocumentType].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.moneyAccountDocumentType].pagination, state[action.payload.moneyAccountDocumentType]?.ids, true)
                }
            })
        });
    }),

    on(MoneyAccountAction.DeleteItemFailure, (state, action) => ({
        ...state,
        [action.payload.moneyAccountDocumentType]: {
            ...state[action.payload.moneyAccountDocumentType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.moneyAccountDocumentType].itemsLoading, action.payload.item.id!, false)
        }
    })),

    on(MoneyAccountAction.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (type: MoneyAccountDocumentType) => adapter.getSelectors((state: MoneyAccountsState) => state[type]).selectAll;
